import { FormEvent, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslations } from '../i18n';
import facebookIcon from '../assets/facebook.png';
import tripadvisorIcon from '../assets/tripadvisor.png';

type OpenHourEntry = { day: string; hours: string };

const NEWSLETTER_ENDPOINT = 'https://formsubmit.co/ajax/check_charly@abv.bg';

export function Footer() {
  const { t } = useTranslations();
  const [email, setEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const restaurantName = t('footer.restaurantName', 'CheckPoint Charly') as string;
  const addressLine1 = t('footer.addressLine1', 'Sofia Center, ul. "Ivan Vazov" 12') as string;
  const country = t('footer.country', 'Bulgaria') as string;
  const viewOnMapLabel = t('footer.viewOnMap', 'View on map') as string;
  const sendMessageLabel = t('footer.sendMessage', 'Send a message') as string;
  const contactEmail = t('footer.contactEmail', 'check_charly@abv.bg') as string;
  const emailLabel = t('footer.emailLabel', 'Email') as string;
  const mobileLabel = t('footer.mobilePhoneLabel', 'Mobile Phone') as string;
  const mobileNumber = t('footer.mobilePhoneNumber', '+359 886 577 342') as string;
  const landlineLabel = t('footer.landlinePhoneLabel', 'Stationary Phone') as string;
  const landlineNumber = t('footer.landlinePhoneNumber', '+359 2 988 03 70') as string;
  const openHoursHeading = t('footer.openHours.heading', 'Open Hours') as string;
  const openHours = (t('footer.openHours.entries', [
    { day: 'Monday', hours: '11:30 AM - 11:30 PM' },
    { day: 'Tuesday', hours: '11:30 AM - 11:30 PM' },
    { day: 'Wednesday', hours: '11:30 AM - 11:30 PM' },
    { day: 'Thursday', hours: '11:30 AM - 11:30 PM' },
    { day: 'Friday', hours: '11:30 AM - 11:30 PM' },
    { day: 'Saturday', hours: '11:30 AM - 11:30 PM' },
    { day: 'Sunday', hours: '11:30 AM - 11:30 PM' },
  ]) as OpenHourEntry[]);
  const menusHeading = t('footer.menusHeading', 'Menus') as string;
  const menuLabel = t('navigation.menu', 'Menu') as string;
  const lunchMenuLabel = t('navigation.lunchMenu', 'Lunch Menu') as string;
  const followUs = t('footer.followUs', 'Follow us') as string;
  const privacyLabel = t('footer.privacy', 'Privacy Policy') as string;
  const termsLabel = t('footer.terms', 'Terms & Conditions') as string;
  const copyright = t('footer.copyright', '© 2025 CheckPoint Charly. All rights reserved.') as string;
  const poweredBy = t('footer.poweredBy', 'Designed & Powered by') as string;
  const newsletter = t('footer.newsletter', {}) as {
    heading: string;
    description: string;
    placeholder: string;
    cta: string;
    success: string;
    error: string;
    subject: string;
  };

  const sanitizePhone = (value: string) => value.replace(/[^\d+]/g, '');

  const handleNewsletterSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email) {
      setNewsletterStatus('error');
      return;
    }

    try {
      setNewsletterStatus('sending');
      const response = await fetch(NEWSLETTER_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          email,
          _subject: newsletter.subject,
          _template: 'table',
          source: 'footer-newsletter',
        }),
      });

      if (!response.ok) {
        throw new Error(`Newsletter signup failed with status ${response.status}`);
      }

      setNewsletterStatus('success');
      setEmail('');
    } catch (error) {
      console.error('Unable to submit newsletter signup', error);
      setNewsletterStatus('error');
    }
  };

  return (
    <footer className="bg-[var(--color-bg-primary)] text-[var(--color-text-primary)]">
      <div className="mx-auto max-w-6xl space-y-12 px-6 pb-28 pt-8 text-sm sm:pt-10 md:pb-14">
        <div className="footer-separator mb-6" aria-hidden="true" />
        <div className="space-y-4 text-center">
          <h3 className="text-2xl font-heading">
            <span className="heading-accent">{newsletter.heading}</span>
          </h3>
          <p className="mx-auto max-w-2xl text-sm text-[var(--color-text-primary)]/70">{newsletter.description}</p>
          <form onSubmit={handleNewsletterSubmit} className="mx-auto flex max-w-xl flex-col gap-3 sm:flex-row">
            <label className="sr-only" htmlFor="footer-newsletter-email">
              {newsletter.heading}
            </label>
            <input
              id="footer-newsletter-email"
              type="email"
              required
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                if (newsletterStatus !== 'idle') {
                  setNewsletterStatus('idle');
                }
              }}
              placeholder={newsletter.placeholder}
              className="flex-1 rounded-full border border-white/10 bg-[var(--color-bg-primary)]/80 px-5 py-3 text-sm text-[var(--color-text-primary)] shadow-inner focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c2673a]"
            />
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#c2673a] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#a85630] disabled:opacity-70"
              disabled={newsletterStatus === 'sending'}
            >
              {newsletterStatus === 'sending' ? (
                <span>Sending…</span>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4"
                  >
                    <path d="M4 12L20 5l-4.5 14L12 14l-4 4z" />
                  </svg>
                  <span>{newsletter.cta}</span>
                </>
              )}
            </button>
          </form>
          {newsletterStatus === 'success' ? (
            <p className="text-sm text-emerald-400">{newsletter.success}</p>
          ) : null}
          {newsletterStatus === 'error' ? (
            <p className="text-sm text-rose-400">{newsletter.error}</p>
          ) : null}
        </div>

        <div className="footer-separator my-12" aria-hidden="true" />

        <div className="grid gap-8 md:grid-cols-2">
          <div className="space-y-4">
            <div className="space-y-1 text-base">
              <h2 className="text-2xl font-heading">{restaurantName}</h2>
              <p>{addressLine1}</p>
              <p>{country}</p>
            </div>
            <div>
              <NavLink
                to="/contact"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="inline-flex items-center justify-center rounded-full border border-[var(--color-text-primary)]/20 px-4 py-2 text-sm font-semibold transition hover:border-transparent hover:bg-[var(--color-text-primary)] hover:text-[var(--color-bg-primary)]"
              >
                {viewOnMapLabel}
              </NavLink>
            </div>
            <div className="space-y-1 text-[var(--color-text-primary)]/85">
              <p>
                <span className="font-semibold text-[var(--color-text-primary)]">{emailLabel}:</span>{' '}
                <a href={`mailto:${contactEmail}`} className="underline-offset-2 transition hover:text-[var(--color-accent)] hover:underline">
                  {contactEmail}
                </a>
              </p>
              <p>
                <span className="font-semibold text-[var(--color-text-primary)]">{mobileLabel}:</span>{' '}
                <a href={`tel:${sanitizePhone(mobileNumber)}`} className="underline-offset-2 transition hover:text-[var(--color-accent)] hover:underline">
                  {mobileNumber}
                </a>
              </p>
              <p>
                <span className="font-semibold text-[var(--color-text-primary)]">{landlineLabel}:</span>{' '}
                <a href={`tel:${sanitizePhone(landlineNumber)}`} className="underline-offset-2 transition hover:text-[var(--color-accent)] hover:underline">
                  {landlineNumber}
                </a>
              </p>
            </div>
            <div>
              <NavLink
                to="/contact#contact-form"
                className="inline-flex items-center justify-center rounded-full border border-[var(--color-text-primary)]/20 px-4 py-2 text-sm font-semibold transition hover:border-transparent hover:bg-[var(--color-text-primary)] hover:text-[var(--color-bg-primary)]"
              >
                {sendMessageLabel}
              </NavLink>
            </div>
          </div>

          <div className="space-y-4 text-base">
            <h3 className="text-xl font-heading">{openHoursHeading}</h3>
            <ul className="space-y-2 text-[var(--color-text-primary)]/85">
              {openHours.map((entry) => (
                <li key={entry.day} className="flex items-baseline justify-between gap-4">
                  <span className="font-semibold text-[var(--color-text-primary)]">{entry.day}:</span>
                  <span>{entry.hours}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="footer-separator mt-12" aria-hidden="true" />

        <div className="grid gap-8 pt-8 md:grid-cols-2">
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">{menusHeading}</h3>
            <nav className="flex flex-col gap-2">
              <NavLink
                to="/menu"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="transition hover:text-[var(--color-accent)]"
              >
                {menuLabel}
              </NavLink>
              <a
                href="https://menu.checkpointcharly.everbranch.co/lunch/index.html"
                target="_blank"
                rel="noopener noreferrer"
                className="transition hover:text-[var(--color-accent)]"
              >
                {lunchMenuLabel}
              </a>
            </nav>
          </div>
          <div className="space-y-6 text-[var(--color-text-primary)]/85">
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">{t('footer.policiesHeading', 'Policies')}</h3>
              <NavLink
                to="/privacy-policy"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="block transition hover:text-[var(--color-accent)]"
              >
                {privacyLabel}
              </NavLink>
              <NavLink
                to="/terms"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="block transition hover:text-[var(--color-accent)]"
              >
                {termsLabel}
              </NavLink>
            </div>
          </div>
        </div>

        <div className="footer-separator mt-12" aria-hidden="true" />

        <div className="pt-8 text-center">
          <div className="flex items-center justify-center gap-3 text-sm uppercase tracking-[0.2em] text-[var(--color-text-primary)]/60">
            <span>{followUs}</span>
            <div className="flex items-center gap-2">
              <a
                href="https://www.facebook.com/checkpoint.charly.9"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('footer.facebookAria', 'Visit CheckPoint Charly on Facebook') as string}
                className="flex h-10 w-10 items-center justify-center transition hover:opacity-80"
              >
                <img src={facebookIcon} alt="CheckPoint Charly on Facebook" className="h-10 w-10" />
              </a>
              <a
                href="https://www.tripadvisor.com/Restaurant_Review-g294452-d1481065-Reviews-Checkpoint_Charly-Sofia_Sofia_Region.html"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('footer.tripadvisorAria', 'Visit CheckPoint Charly on Tripadvisor') as string}
                className="flex h-10 w-10 items-center justify-center transition hover:opacity-80"
              >
                <img src={tripadvisorIcon} alt="CheckPoint Charly on Tripadvisor" className="h-10 w-10" />
              </a>
            </div>
          </div>
        </div>

        <div className="footer-separator mt-12" aria-hidden="true" />

        <div className="pt-8 text-center text-sm text-[var(--color-text-primary)]/80">
          <p>{copyright}</p>
          <p>
            {poweredBy}{' '}
            <a href="https://everbranch.co" target="_blank" rel="noopener noreferrer" className="underline transition hover:text-[var(--color-accent)]">
              EverBranch
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
